import { memo, useEffect, useState } from "react";
import { MiAutocomplete, MiButton } from "@miplatform/components";
import { BUTTON_COLORS, ICONS_WEB } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { MenuItem, Paper, Popper, TextField, Typography } from "@mui/material";
import { AddCityModal } from "../../AddCityModal/AddCityModal";

export const CityAutocomplete = ({
  value,
  cities,
  onChange,
  onCreate = () => {},
  getCities = () => {},
  isReadOnly,
  ...props
}) => {
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [isAddCityModalOpen, setIsAddCityModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const handleSearch = (_e, value) => setSearch(value);

  const handleCreate = () => {
    onCreate();
    setIsAddCityModalOpen(false);
  };

  const handleAutoCompleteOpen = () => setIsAutocompleteOpen(true);

  const handleAutoCompleteClose = () => setIsAutocompleteOpen(false);

  const handleAddCityModalOpen = () => setIsAddCityModalOpen(true);

  const handleAddCityModalClose = () => setIsAddCityModalOpen(false);

  useEffect(() => {
    getCities(debouncedSearch);
  }, [debouncedSearch]);

  return (
    <>
      <MiAutocomplete
        size="small"
        fullWidth
        disableClearable
        forcePopupIcon={false}
        open={isAutocompleteOpen}
        filterOptions={(o) => o}
        options={cities}
        getOptionLabel={(value) => {
          const city =
            typeof value === "string"
              ? cities.find((city) => city.id === value)
              : value;

          return city?.cityName ? city.cityName : "";
        }}
        value={value}
        inputValue={search}
        readOnly={isReadOnly}
        getOptionKey={({ id }) => id}
        onInputChange={handleSearch}
        isOptionEqualToValue={(value, option) =>
          typeof value === "string"
            ? value === option?.id
            : option?.id === value?.id
        }
        onChange={(_e, value) => onChange(value)}
        onOpen={handleAutoCompleteOpen}
        onClose={handleAutoCompleteClose}
        renderOption={({ ...props }, { id, ...option }) => (
          <MenuItem key={id} {...props}>
            <Typography
              lineHeight={2}
            >{`${option.cityName}, ${option.regionCode}`}</Typography>
            <Typography
              color="gray"
              ml={1}
            >{`(${option.countyName})`}</Typography>
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            shrunk
            required
            label="City"
            InputProps={{
              ...params.InputProps,
              value: search,
            }}
          />
        )}
        slotProps={{
          paper: {
            onMouseDown: handleAddCityModalOpen,
          },
        }}
        PopperComponent={AutocompletePopper}
        PaperComponent={CityAutocompletePaper}
        {...props}
      />
      <AddCityModal
        open={isAddCityModalOpen}
        onClose={handleAddCityModalClose}
        onCreate={handleCreate}
      />
    </>
  );
};

export const CityAutocompletePaper = memo(
  ({ children, onMouseDown, ...props }) => (
    <Paper {...props}>
      {children}
      <MiButton
        fullWidth
        title="Add City"
        color={BUTTON_COLORS.BLUE}
        startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
        sx={{
          borderRadius: 0,
          justifyContent: "flex-start",
          color: "blue.main",
          pl: 4,
        }}
        onMouseDown={onMouseDown}
      />
    </Paper>
  )
);

const AutocompletePopper = memo((props) => (
  <Popper
    {...props}
    style={{ width: "fit-content" }}
    placement="bottom-start"
  />
));
